import { useState, useEffect } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Search, RefreshCw, ChevronRight } from "lucide-react";

const sevColors = { error: "#EF4444", warn: "#F59E0B", info: "#29B5E8", debug: "#5A6B82" };

export default function LogsExplorerPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sevFilter, setSevFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (sevFilter !== "all") params.severity = sevFilter;
      const { data } = await api.get("/logs", { params });
      setLogs(data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };
  useEffect(() => { fetchLogs(); }, [sevFilter]);

  return (
    <div className="p-6 space-y-4" data-testid="logs-explorer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Logs Explorer</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{logs.length} log events</p>
        </div>
      </div>

      <div className="flex items-center gap-3" data-testid="logs-filters">
        <div className="relative flex-1 max-w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#6B7280" }} />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchLogs()} placeholder="Search logs... (Enter to search)" className="pl-9 h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142] placeholder:text-[#3A4A5E]" data-testid="logs-search" />
        </div>
        <Select value={sevFilter} onValueChange={setSevFilter}>
          <SelectTrigger className="w-[130px] h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#8899AA]" data-testid="logs-severity-filter"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }}>
            <SelectItem value="all">All Levels</SelectItem><SelectItem value="error">Error</SelectItem><SelectItem value="warn">Warning</SelectItem><SelectItem value="info">Info</SelectItem><SelectItem value="debug">Debug</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="rounded-lg border-gray-200 text-[#8899AA]" onClick={fetchLogs}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
      </div>

      <div className="sf-card overflow-hidden" data-testid="logs-table">
        {loading ? <Skeleton className="h-96 bg-white/5" /> : (
          <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.03)" }}>
            {logs.map((log) => (
              <button key={log.log_id} onClick={() => setSelectedLog(log)} className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-black/[0.01] transition-colors" data-testid={`log-row-${log.log_id}`}>
                <div className="w-1.5 h-6 rounded-full flex-shrink-0" style={{ background: sevColors[log.severity] || "#5A6B82" }} />
                <span className="text-[10px] font-mono w-[130px] flex-shrink-0" style={{ color: "#9CA3AF" }}>{log.timestamp?.slice(0, 19).replace("T", " ")}</span>
                <Badge className="text-[8px] uppercase font-bold border-0 rounded w-[50px] text-center flex-shrink-0" style={{ background: `${sevColors[log.severity]}15`, color: sevColors[log.severity] }}>{log.severity}</Badge>
                <span className="text-[11px] font-mono flex-shrink-0 w-[160px]" style={{ color: "#6B7280" }}>{log.source}</span>
                <span className="text-[12px] text-[#8899AA] truncate flex-1">{log.message}</span>
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#9CA3AF" }} />
              </button>
            ))}
            {logs.length === 0 && <div className="py-12 text-center text-[12px] font-mono" style={{ color: "#9CA3AF" }}>No logs match your search</div>}
          </div>
        )}
      </div>

      <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <SheetContent className="w-full sm:max-w-[500px] overflow-auto" style={{ background: "#FFFFFF", borderLeft: "1px solid #E8E4DF" }} data-testid="log-detail-sheet">
          {selectedLog && (
            <>
              <SheetHeader><SheetTitle className="text-[#2D3142] text-sm">Log Detail</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className="text-[9px] uppercase font-bold border-0" style={{ background: `${sevColors[selectedLog.severity]}15`, color: sevColors[selectedLog.severity] }}>{selectedLog.severity}</Badge>
                  <span className="text-[10px] font-mono" style={{ color: "#6B7280" }}>{selectedLog.log_id}</span>
                </div>
                <div><label className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Message</label><p className="text-[13px] text-[#2D3142] mt-1">{selectedLog.message}</p></div>
                <div><label className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Source</label><p className="text-[13px] font-mono text-[#2D3142] mt-1">{selectedLog.source}</p></div>
                <div><label className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Timestamp</label><p className="text-[13px] font-mono text-[#2D3142] mt-1">{selectedLog.timestamp}</p></div>
                {selectedLog.trace_id && <div><label className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Trace ID</label><p className="text-[13px] font-mono mt-1" style={{ color: "#1E3A5F" }}>{selectedLog.trace_id}</p></div>}
                <div>
                  <label className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>JSON Payload</label>
                  <pre className="mt-1 p-3 rounded-lg text-[11px] font-mono text-[#8899AA] overflow-auto" style={{ background: "rgba(45,49,66,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>{JSON.stringify(selectedLog.payload, null, 2)}</pre>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
